package nl.volleyplanner.web.controller

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.create
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.base.RootPageController
import nl.volleyplanner.web.base.WebPageController
import nl.volleyplanner.web.base.icon
import org.w3c.dom.HTMLElement

data class WebPageBreadcrumbItem(val link: String, val text: String)

class VolleyplannerWebPageIndexController : RootPageController() {
    override val name = "WebPageController"

    override val path = ""
    override val requiredAuthLevel = AuthLevel.AnonymousPublic
    override val title = "Volleyplanner"
    override val childPages = listOf<WebPageController>(
        ProductDetailsController(this),
        RegisterController(this)
    )

    override fun createRuntimeInstance() = VolleyplannerWebPageIndexController()

        override suspend fun renderContent(): HTMLElement {
        return document.create.div {
            // Jumbotron 1
            div(classes = "p-5 mb-4 bg-body-tertiary rounded-3") {
                div (classes = "container-fluid py-5") {
                    h1 (classes = "display-5 fw-bold") { +"Volleyplanner" }
                    p (classes = "col-md-8 fs-4") {
                        unsafe { +"Ook zo'n gepuzzel bij het inplannen van scheidsrechters en tellers? Leden per ongeluk tijdens hun uitwedstrijd thuis ingedeeld? " }
                        unsafe { +"Nu al in de stress om in januari de planning op tijd rond te krijgen?<br />" }
                    }
                    p (classes = "col-md-8 fs-4") {
                        unsafe { +"Zoek niet verder en <strong>gebruik de volleyplanner!</strong>" }
                    }
                    a (classes = "btn btn-primary px-2 me-md-2") {
                        role = "button"
                        href = "/registreren"
                        +"Aan de slag!"
                    }
                    a (classes = "btn btn-outline-secondary px-2") {
                        role = "button"
                        href = "/product-details"
                        +"Meer informatie"
                    }
                }
            }
            div (classes = "container px-4 py-5") {
                h2 (classes = "pb-2 border-bottom") { +"Eenvoudig aan de slag!" }
                div (classes = "row row-cols-1 row-cols-md-2 align-items-md-center g-5 py-5") {
                    div (classes = "col d-flex flex-column align-items-start gap-2") {
                        h2 (classes = "fw-bold text-body-emphasis") {
                            +"Jouw planning in 4 stappen"
                        }
                        p (classes = "text-body-secondary") {
                            unsafe { +"De planner deelt leden in als scheidsrechter of teller bij thuiswedstrijden. Rekening houdend met jullie beleid en wensen. " }
                            unsafe { +"Vooral voor (middel)grote verenigingen met thuiszaterdagen en veel zaken om rekening mee te houden biedt dit een uitkomst. " }
                        }
                        p (classes = "text-body-secondary") {
                            +"Heb je alle gegevens bij de hand? Dan heb je binnen een uur een goede eerste versie klaar!"
                        }
                        div (classes = "d-grid gap-2 d-md-flex justify-content-md-start") {
                            a (classes = "btn btn-primary px-2 me-md-2") {
                                role = "button"
                                href = "/registreren"
                                +"Aan de slag!"
                            }
                            a (classes = "btn btn-outline-secondary px-2") {
                                role = "button"
                                href = "/product-details"
                                +"Meer informatie"
                            }
                        }
                    }
                    div (classes = "col") {
                        div (classes = "row row-cols-1 row-cols-sm-2 g-4") {
                            div (classes = "col d-flex flex-column gap-2") {
                                div (classes = "feature-icon-small d-inline-flex align-items-center justify-content-center text-bg-primary bg-gradient fs-4 rounded 3") {
                                    icon("bi-gear-fill", useMargin = false)
                                }
                                h5 (classes ="fw-semibold mb-0 text-body-emphasis") {
                                    +"1. Configureer beleid"
                                }
                                p (classes = "text-body-secondary") {
                                    +"Configureer groepen scheidsrechters, wie welk team mag fluiten en tellen etc."
                                }
                            }
                            div (classes = "col d-flex flex-column gap-2") {
                                div (classes = "feature-icon-small d-inline-flex align-items-center justify-content-center text-bg-primary bg-gradient fs-4 rounded 3") {
                                    icon("bi-file-earmark-spreadsheet-fill", useMargin = false)
                                }
                                h5 (classes ="fw-semibold mb-0 text-body-emphasis") {
                                    +"2. Importeer gegevens"
                                }
                                p (classes = "text-body-secondary") {
                                    +"Upload een spreadsheet met de namen en teams van jullie leden. Wedstrijdgegevens worden automatisch opgehaald bij de Nevobo."
                                }
                            }
                            div (classes = "col d-flex flex-column gap-2") {
                                div (classes = "feature-icon-small d-inline-flex align-items-center justify-content-center text-bg-primary bg-gradient fs-4 rounded 3") {
                                    icon("bi-cpu-fill", useMargin = false)
                                }
                                h5 (classes ="fw-semibold mb-0 text-body-emphasis") {
                                    +"3. Draai planner"
                                }
                                p (classes = "text-body-secondary") {
                                    +"In 5 a 10 minuten maakt de planner het meest optimale schema aan de hand van jullie configuratie."
                                }
                            }
                            div (classes = "col d-flex flex-column gap-2") {
                                div (classes = "feature-icon-small d-inline-flex align-items-center justify-content-center text-bg-primary bg-gradient fs-4 rounded 3") {
                                    icon("bi-cloud-arrow-up-fill", useMargin = false)
                                }
                                h5 (classes ="fw-semibold mb-0 text-body-emphasis") {
                                    +"4. Publiceren"
                                }
                                p (classes = "text-body-secondary") {
                                    +"Maak optioneel handmatige aanpassingen en deel het schema met je leden."
                                }
                            }
                        }
                    }
                }
            }
            div (classes = "row align-items-md-stretch") {
                div (classes = "col-md-6") {
                    div (classes = "h-100 p-5 text-bg-primary rounded-3") {
                        h2 { +"Vrijblijvende pilot" }
                        p {
                            unsafe { +"Vanuit een uit de hand gelopen hobby is een eerste versie om de indeling van scheidsrechters en tellers makkelijker te maken is in september met veel plezier ontvangen bij mijn eigen vereniging." }
                        }
                        p {
                            unsafe { +"Voor de <strong>tweede competitiehelft lanceer ik een vrijblijvende pilot</strong> om te kijken of de volleyplanner ook wat voor andere verenigingen kan betekenen." }
                        }
                    }
                }
                div (classes = "col-md-6") {
                    div (classes = "h-100 p-5 bg-body-tertiary border rounded-3") {
                        h2 { +"AVG vriendelijk " }
                        p {
                            +"We verwerken geen informatie die ook niet nodig is. Per lid is alleen hun naam en het team waar ze in spelen noodzakelijk om aan de slag te kunnen."
                        }
                        p {
                            +"In het uiteindelijke schema worden namen afgekort tot de voornaam + team."
                        }
                    }
                }
            }
        }
    }
}